import * as React from 'react';
import { ExternalLink, fieldValidation, PortalSharedExternalLinkView, rng } from '../../amorg-common';
import { createExternalLink, deleteExternalLink, getExternalLinks, updateExternalLink } from '../../api/externalLinkClientApi';
import { Flex, Grid } from '../../designSystem/compositeComponents/FlexHelpers';
import { Icon, iconName, iconNames } from '../../designSystem/icons/Icons';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { H3 } from '../../designSystem/typography/Heading';
import { clientAccessHelper } from '../../utils/clientAccessHelper';

import styled from 'styled-components';
import { portalClientApi } from '../../api/portalClientApi';
import { sharedPortalResourceClientApi } from '../../api/portalManagementClientApi';
import { Button } from '../../designSystem/button/Button';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { IconSelectionPanelWithButton } from '../../designSystem/compositeComponents/IconSelectionPanel';
import { TextField } from '../../designSystem/formField/text/TextField';
import { TextAreaField } from '../../designSystem/formField/textArea/TextAreaField';
import { OnOff } from '../../designSystem/inputs/OnOff';
import { Modal } from '../../designSystem/Modal';
import { ToggletipTheOne } from '../../designSystem/ToggletipTheOne';
import { P } from '../../designSystem/typography/Body';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';


export function ExternalLinksOverviewPage (): JSX.Element
{
    return (
        <MemberPageWrapper selectedMenuItemKey={'externallinks'} breadCrumbs={[{ text: 'Link samling', url: '' }]}>
            <PageHeader 
                narrow
                headerIcon='interface_actions_link'
                headerText='Link samling' />
            <SingleColumnLayout narrow>
                <ExternalLinksOverviewPageContent />
            </SingleColumnLayout>
        </MemberPageWrapper>
    );
}

function ExternalLinksOverviewPageContent (): JSX.Element
{
    const [loading, setLoading] = React.useState(true);
    const [links, setLinks] = React.useState<ExternalLink[]>([]);
    const [portalLinks, setPortalLinks] = React.useState<PortalSharedExternalLinkView[]>([]);

    React.useEffect(() =>
    {
        refreshLinks();
    }, []);

    async function refreshLinks()
    {
        setLoading(true);

        const links = await getExternalLinks();
        setLinks(links);

        const portalLinks = (await portalClientApi.getPortalSharedResources())
            .filter(r => r.resourceType === 'ExternalLink') as PortalSharedExternalLinkView[];
        setPortalLinks(portalLinks);

        setLoading(false);
    }

    const protectedUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(protectedUser, ['ViewCustomFormAnswerList']);

    return (

        <Flex flexDirection='column' gap={16}>
                <LoadingPageOverlay loading={loading} />

                {!hasAccess
                    ? <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>
                    :
                    <>
                    <CreateLinkModalWithButton onCreated={refreshLinks} />

                    <Grid gap={16} gridTemplateColumns={2}>
                        {links.map(link => (
                            <ExternalLinkCard key={link.id} 
                                link={link} 
                                portalResourceId={portalLinks.find(pl => pl.resourceId === link.id)?.id || ""}
                                onEdited={refreshLinks} 
                            />
                        ))}
                    </Grid>
                    </>
                }
            </Flex>
    );
}


function ExternalLinkCard({ link, portalResourceId, onEdited }: { link: ExternalLink, portalResourceId: string, onEdited: () => void; }): JSX.Element
{
    const ctx = React.useContext(ProtectedPageContext);

    let adjustedLink = link.url;
    if(!adjustedLink.startsWith('http://') && !adjustedLink.startsWith('https://'))
    {
        adjustedLink = 'https://' + adjustedLink;
    }

    return (
        <ExternalLinkCardStyled key={link.id}>
        
            <Flex flexDirection='column' gap={8} justifyContent='space-between' style={{height:'100%'}}>
                <Flex flexDirection='column' gap={8} justifyContent='space-between' >
                    <Flex flexDirection="row" alignItems='center' justifyContent='space-between' width='100%'>
                        <Flex flexDirection="row" alignItems='center' gap={8}>
                            <Icon name={link.iconName as iconName || "interface_actions_externalLink"} size='small'/>
                            <H3>{link.title}</H3>
                        </Flex>
                        <EditLinkModalWithButton link={link} onEdited={onEdited} />
                    </Flex>
                
                    <P dangerouslySetInnerHTML={{ __html: link.description.replace(/\n/g, '<br />') }} />
                </Flex>
                <Flex flexDirection='row' gap={8} justifyContent='space-between' width='100%'>
                    <LinkButton href={adjustedLink} target="_blank" variant='light' icon='interface_actions_externalLink' text='Åben link' />

                    <Flex gap={4} alignItems='center'>
                        <ToggletipTheOne icon='captive_portal' iconColor={portalResourceId ? ctx.colors.icon.active : ctx.colors.icon.passive}>
                            <span>delt i medarbejderportalen</span>
                        </ToggletipTheOne>
                        <OnOff active={!!portalResourceId}
                            onClick={async (active)=>{
                                if(active)
                                    await sharedPortalResourceClientApi.sharePortalResource(
                                        rng.getRandomId(),
                                        link.id,
                                        {
                                            resourceType: 'ExternalLink',
                                            config: {}
                                        }
                                    );
                                else
                                    await sharedPortalResourceClientApi.unsharePortalResource(portalResourceId);
                                onEdited && onEdited();
                            }} 
                        />
                    </Flex>
                </Flex>
            </Flex>
        
        </ExternalLinkCardStyled>
    );
}

type CreateLinkModalWithButtonProps = {
    onCreated:()=>void
}
function CreateLinkModalWithButton(props:CreateLinkModalWithButtonProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [inEditLink, setInEditLink] = React.useState<ExternalLink>({
        id: "",
        title: "",
        description: "",
        url: "",
        iconName: "actions_add",
        showInMenu: false
    });
    const [showValidation, setShowValidation] = React.useState(false);

    function handleShow()
    {
        setInEditLink({
            id: "",
            title: "",
            description: "",
            url: "",
            iconName: "actions_add",
            showInMenu: false
        });
        setShowValidation(false);
        setShowModal(true);
    }

    function handleClose()
    {
        setShowModal(false);
    }

    async function handleClickCreate()
    {
        setShowValidation(true);

        if(fieldValidation.externalLink.title(inEditLink.title)

            && fieldValidation.externalLink.description(inEditLink.description)
            && fieldValidation.externalLink.url(inEditLink.url)
            && fieldValidation.externalLink.showInMenu(inEditLink.showInMenu))
        {
            setLoading(true);
            const result = await createExternalLink({...inEditLink, id:rng.getRandomId() });
            setLoading(false);

            if(result.isSuccess)
            {
                handleClose();
                props.onCreated();
            }

        }
    }

    return (
        <>

        <Flex flexDirection='row' justifyContent='flex-end'>
            <Button variant='filled' icon="actions_plus" size={"42px"} emphasize onClick={handleShow} text='Opret nyt link'  />
        </Flex>

        <LoadingPageOverlay loading={loading} />
        <Modal
            title="Opret nyt link"
            showModal={showModal}
            onCancel={handleClose}
            content={

                <Flex flexDirection='column' gap={16}>
                    <ExternalLinkEditor
                        link={inEditLink}
                        showValidation={showValidation}
                        onChange={(externalLink)=>setInEditLink(externalLink)}
                    />

                    <Button variant='filled' onClick={handleClickCreate} text='Opret link' />

                </Flex>
            }
        />
        </>
    );
}

type EditLinkModalWithButtonProps = {
    link:ExternalLink,
    onEdited:()=>void
}
function EditLinkModalWithButton(props:EditLinkModalWithButtonProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [inEditLink, setInEditLink] = React.useState<ExternalLink>({
        id: "",
        title: "",
        description: "",
        url: "",
        iconName: "actions_add",
        showInMenu: false
    });
    const [showValidation, setShowValidation] = React.useState(false);

    React.useEffect(() => {
        setInEditLink(props.link);
    }, [props.link]);

    function handleShow()
    {
        setInEditLink(props.link);
        setShowValidation(false);
        setShowModal(true);
    }

    function handleClose()
    {
        setShowModal(false);
    }

    async function handleClickEdit()
    {
        setShowValidation(true);

        if(fieldValidation.externalLink.title(inEditLink.title)

            && fieldValidation.externalLink.description(inEditLink.description)
            && fieldValidation.externalLink.url(inEditLink.url)
            && fieldValidation.externalLink.showInMenu(inEditLink.showInMenu))
        {
            setLoading(true);
            const result = await updateExternalLink(inEditLink);
            setLoading(false);

            if(result.isSuccess)
            {
                handleClose();
                props.onEdited();
            }

        }
    }

    async function handleDelete()
    {
        handleClose();
        await deleteExternalLink(props.link.id);
        props.onEdited();
        
    }


    return (
        <>

        <Button variant='outlined' onClick={handleShow} icon='interface_actions_edit' />

        <LoadingPageOverlay loading={loading} />
        <Modal
            title="Rediger link"
            showModal={showModal}
            onCancel={handleClose}
            content={
                <Flex flexDirection='column' gap={16} justifyContent='space-between'>
                    <ExternalLinkEditor
                        link={inEditLink}
                        showValidation={showValidation}
                        onChange={(externalLink)=>setInEditLink(externalLink)}
                    />

                    <Flex flexDirection='row' gap={8} justifyContent='space-between'>
                        <Button variant="destructive" onClick={handleDelete} text="Slet" />
                        <Button variant='filled' onClick={handleClickEdit} text='Gem link' />
                    </Flex>
                </Flex>
            }
        />
        </>
    );
}

type ExternalLinkEditorProps = {
    link:ExternalLink,
    showValidation:boolean,
    onChange:(externalLink:ExternalLink)=>void
}
function ExternalLinkEditor(props:ExternalLinkEditorProps):JSX.Element
{
    return (
        <Flex flexDirection='column' gap={8}>

            <IconSelectionPanelWithButton
                icons={iconNames as unknown as iconName[]}
                selectedIcon={props.link.iconName as iconName || undefined}
                onIconSelected={(icon) => {
                    props.onChange({ ...props.link, iconName: icon })
                }}
            />

            <TextField
                reserveMessageSpace
                label="Titel"
                value={props.link.title}
                onChange={(value) => { props.onChange({ ...props.link, title:value })}}
                validator={(value)=> !props.showValidation || fieldValidation.externalLink.title(value)
                        ? { valid:true }
                        : { valid:false, errorMessage:`Titel skal udfyldes. Må maks bestå af 100 tegn (${value.length})` }
                }
            />

            <TextAreaField
                reserveMessageSpace
                label="Beskrivelse"
                value={props.link.description}
                onChange={(value) => { props.onChange({ ...props.link, description:value })}}
                validator={(value)=> !props.showValidation ||fieldValidation.externalLink.description(value)
                        ? { valid:true }
                        : { valid:false, errorMessage:`Beskrivelse skal udfyldes. Må maks bestå af 10000 tegn (${value.length})` }
                }
            />


            <TextField
                reserveMessageSpace
                label="url"
                value={props.link.url}
                onChange={(value) => { props.onChange({ ...props.link, url:value })}}
                validator={(value)=> !props.showValidation || fieldValidation.externalLink.url(value)
                        ? { valid:true }
                        : { valid:false, errorMessage:`URL skal udfyldes med en gyldig webaddresse der starter med https:// og er maks 500 tegn (${value.length})` }
                }
            />

            {/* <Flex flexDirection='row' gap={8} alignItems='center'>
                <P>Vis i menu:</P>
                <OnOff active={props.link.showInMenu} variant={"fullColor"} onClick={(active)=>props.onChange({...props.link, showInMenu:active})} size="medium" />
            </Flex> */}

        </Flex>
    );
}


const ExternalLinkCardStyled = styled.div`

    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);

    padding: 16px;

    .edit-icon {
        opacity: 0;
        transition: opacity 0.2s;
    }
    &:hover .edit-icon {
        opacity: 1;
    }
`